import { PAIRING_HOURS } from '@/demo/content/allocation'
import { RATING_SCALE } from '@/demo/content/personProfile'
import { SKILL_ROWS, personHref, type SkillMatrixRow } from '@/demo/content/skillsMatrix'
import {
  MATCH_CANDIDATES,
  PROJECTS,
  ROLES,
  agentById,
  personById,
  type MatchCandidate,
} from '@/demo/data'

/**
 * Surface 1.9 — Badges. Every figure, label, route and sentence the page
 * renders resolves through this module, `@/demo/data`, or the surfaces this
 * one is downstream of; the component carries no literal.
 *
 * WHAT A BADGE IS (product brief): not a certificate. A badge is a course
 * record plus supervised deliveries plus an expert sign-off, and what it buys
 * is mechanical — it changes what PX may allocate to a person and at which
 * rate. That closes the loop the growth surfaces open: the skills matrix says
 * what is thin, the training center teaches it, the badge proves it, and the
 * allocation engine can then route the work.
 *
 * WHAT IS AUTHORED HERE (once): the five-level ladder, the six badge
 * definitions, and — for the two badges that are not a tracked group skill
 * (the version badge and the agent-review badge) — their holder counts.
 *
 * WHAT IS DERIVED, so this page cannot contradict the ones either side of it:
 *  - a skill badge's holder count IS that skill's `certified` figure in the
 *    matrix, and its named holders ARE the matrix's own certified holders
 *    (a holder the matrix qualifies as "assisted" is not counted as a holder);
 *  - the expert who signs off IS the seeded `expert` role, not a typed name;
 *  - the agent badge's unlock IS that agent's own review gate;
 *  - the allocation panel IS the seeded open-task match — the same two people,
 *    the same two tags, the same two rates;
 *  - the expiry panel's version base IS the client estate in the seed.
 */

/* ── Routes (no href is built by hand in the component) ─────────────────── */

export const ALLOCATIONS_HREF = '/resources/allocations'
export const SKILLS_HREF = '/resources/skills'
export const TRAINING_HREF = '/resources/training'
export const CLIENTS_HREF = '/clients'

export { personHref }

/** A run of prose in which some words are real destinations. */
export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'link'; label: string; href: string }

/* ── Chrome ─────────────────────────────────────────────────────────────── */

export const BADGES_CHROME = {
  moduleLabel: 'PX · Resources',
  breadcrumb: 'Growth › Badges',
  contextPill: 'LEARNER → LEAD',
} as const

/* ── 1. The ladder ──────────────────────────────────────────────────────── */

export type LevelKey = 'enrolled' | 'learner' | 'assisted' | 'certified' | 'lead'

export interface Level {
  key: LevelKey
  label: string
  /** The verb the level carries; rendered after the label, never instead of it. */
  qualifier: string | null
  meaning: string
}

export const LEVELS: readonly Level[] = [
  {
    key: 'enrolled',
    label: 'Enrolled',
    qualifier: null,
    meaning: 'holds a seat on the course; PX routes no work on the strength of it',
  },
  {
    key: 'learner',
    label: 'Learner',
    qualifier: 'observes',
    meaning: 'sits with the delivery and bills no hours against it',
  },
  {
    key: 'assisted',
    label: 'Assisted',
    qualifier: 'works paired',
    meaning: 'delivers the task with a holder reviewing the hours',
  },
  {
    key: 'certified',
    label: 'Certified',
    qualifier: 'owns the task',
    meaning: 'PX may route the task unsupervised, at the certified band',
  },
  {
    key: 'lead',
    label: 'Lead',
    qualifier: 'reviews others',
    meaning: 'signs off the deliveries of other holders on this badge',
  },
] as const

export const levelLabel = (level: Level): string =>
  level.qualifier === null ? level.label : `${level.label} · ${level.qualifier}`

const levelByKey = (key: LevelKey): Level => {
  const level = LEVELS.find((candidate) => candidate.key === key)
  if (level === undefined) throw new Error(`Unknown badge level: ${key}`)
  return level
}

export const ASSISTED = levelByKey('assisted')
export const CERTIFIED = levelByKey('certified')
export const LEAD = levelByKey('lead')

/**
 * Where one seeded person stands today. Guarded against the pool: the marker
 * would otherwise be a claim about Arta that her own record does not make.
 */
const MARKER_PERSON = personById('arta-berisha')
const MARKER_LEVEL: LevelKey = 'assisted'
if (!MARKER_PERSON.skills.some((skill) => skill.includes(MARKER_LEVEL))) {
  throw new Error(
    `${MARKER_PERSON.name} is not seeded as ${MARKER_LEVEL} — the ladder marker would outrun the pool`
  )
}

export const LADDER_MARKER = {
  personId: MARKER_PERSON.id,
  levelKey: MARKER_LEVEL,
  label: `${MARKER_PERSON.name} · here today`,
  href: personHref(MARKER_PERSON.id),
  note: `paired on the migration task with ${PAIRING_HOURS} h of review`,
} as const

export const LADDER = {
  label: 'THE LADDER',
  lede: 'A badge is never simply held: it is held at a level, and the level is what PX reads before it routes an hour.',
} as const

/* ── 2. The badges ──────────────────────────────────────────────────────── */

/** Who signs a badge off — the seeded expert role, never a typed name. */
const EXPERT_PERSON_ID = ROLES.find((role) => role.key === 'expert')?.personId
if (EXPERT_PERSON_ID === undefined) {
  throw new Error('No expert role in the seed — a badge sign-off would have no owner')
}
export const EXPERT = personById(EXPERT_PERSON_ID)

/** The agent whose review gate one badge clears. */
const MAPPING_AGENT = agentById('mapping-generator')

export type Expiry = 'With the Odoo version' | 'Per agent version' | null

interface BadgeSeed {
  key: string
  /**
   * Only for a badge that is not a tracked skill, or whose subject the
   * training center names differently — otherwise the skill's own name is the
   * badge's name, so the two surfaces cannot drift apart.
   */
  name?: string
  /** The tracked group skill this badge certifies — holders derive from it. */
  skillKey?: string
  /** Holders for the two badges that are not a tracked skill, authored once. */
  authoredHolders?: { count: number; namedIds: readonly string[] }
  /** Named holders who hold the badge at Lead rather than Certified. */
  leadIds?: readonly string[]
  course: string
  supervised: number
  supervisedUnit: string
  unlocks: string
  reviewRights: string
  expires: Expiry
}

const BADGE_SEEDS: readonly BadgeSeed[] = [
  {
    key: 'odoo-data-migration',
    skillKey: 'odoo-data-migration',
    course: 'Certified migration engineer',
    supervised: 3,
    supervisedUnit: 'trial loads',
    unlocks: 'Migration tasks routed unsupervised, at the certified band',
    reviewRights: 'Reviews Assisted loads — the pairing hours on a growth move',
    expires: 'With the Odoo version',
  },
  {
    key: 'odoo-19-developer',
    name: 'Odoo 19 developer',
    authoredHolders: { count: 4, namedIds: ['pieter-bakker', 'daan-visser'] },
    leadIds: ['pieter-bakker'],
    course: 'Odoo 19 delta certification',
    supervised: 2,
    supervisedUnit: 'build tasks on 19',
    unlocks: 'Build tasks on an Odoo 19 rollout',
    reviewRights: 'Leads review build output before it merges',
    expires: 'With the Odoo version',
  },
  {
    key: 'payroll-be',
    skillKey: 'payroll-be',
    leadIds: ['margot-claes'],
    // The catalogue carries no payroll course on purpose: the certification is
    // external, so this badge is earned outside the Competence Center.
    course: 'External payroll certification',
    supervised: 2,
    supervisedUnit: 'payroll runs',
    unlocks: 'Payroll scope on Belgian rollouts, at the specialist band',
    reviewRights: 'Lead signs off every other holder',
    expires: null,
  },
  {
    key: 'qa-regression',
    skillKey: 'qa-regression',
    course: 'Regression testing essentials',
    supervised: 4,
    supervisedUnit: 'test passes',
    unlocks: 'Owns a regression pass inside a test phase',
    reviewRights: 'Reviews Assisted testers on the same pass',
    expires: null,
  },
  {
    key: 'retail-data-models',
    name: 'Retail data models',
    skillKey: 'retail-vertical',
    leadIds: ['marc-dubois'],
    course: 'Retail data models',
    supervised: 2,
    supervisedUnit: 'retail rollouts',
    unlocks: 'Retail scope in Discover and Design',
    reviewRights: 'Leads sign the blueprint section',
    expires: null,
  },
  {
    key: 'agent-review-mapping',
    name: `Agent review · ${MAPPING_AGENT.name}`,
    authoredHolders: { count: 3, namedIds: ['julien-roux', 'marc-dubois'] },
    leadIds: ['marc-dubois'],
    course: 'Agent contract & guardrails',
    supervised: 5,
    supervisedUnit: 'reviewed runs',
    unlocks: `Clears the ${MAPPING_AGENT.reviewGate.toLowerCase()} gate on ${MAPPING_AGENT.produces.toLowerCase()}`,
    reviewRights: 'Signs off agent output before it reaches a client system',
    expires: 'Per agent version',
  },
] as const

/** Named holders shown in the table; the rest stand behind the count. */
export const MAX_NAMED_HOLDERS = 2

export interface HolderLink {
  id: string
  name: string
  href: string
  level: string
}

export interface Holders {
  count: number
  named: readonly HolderLink[]
  /** Holders behind the count that the row does not name. */
  moreCount: number
}

const skillRowFor = (key: string): SkillMatrixRow => {
  const row = SKILL_ROWS.find((candidate) => candidate.key === key)
  if (row === undefined) throw new Error(`No tracked skill behind the badge: ${key}`)
  return row
}

const toHolders = (seed: BadgeSeed, ids: readonly string[], count: number): Holders => {
  const { leadIds = [] } = seed
  const isLead = (id: string): boolean => leadIds.includes(id)
  const named = [...ids]
    .sort((a, b) => Number(isLead(b)) - Number(isLead(a)))
    .slice(0, MAX_NAMED_HOLDERS)
    .map((id) => {
      const person = personById(id)
      return {
        id,
        name: person.name,
        href: personHref(id),
        level: isLead(id) ? LEAD.label : CERTIFIED.label,
      }
    })
  if (named.length > count) {
    throw new Error(`Badge ${seed.key} names more holders than it has: ${named.length} of ${count}`)
  }
  return { count, named, moreCount: count - named.length }
}

const holdersOf = (seed: BadgeSeed): Holders => {
  const { skillKey, authoredHolders } = seed
  if (skillKey !== undefined) {
    const row = skillRowFor(skillKey)
    // The matrix marks an unfinished holder with a qualifier ("· assisted");
    // that person is on the ladder, not on the badge.
    const certifiedIds = row.holders.filter((h) => h.label === h.name).map((h) => h.id)
    return toHolders(seed, certifiedIds, row.certified)
  }
  if (authoredHolders !== undefined) {
    return toHolders(seed, authoredHolders.namedIds, authoredHolders.count)
  }
  throw new Error(`Badge ${seed.key} states no holders`)
}

export interface BadgeRow {
  key: string
  /** The badge's own name — the skill's name unless the seed overrides it. */
  name: string
  /** Course · n supervised deliveries · expert sign-off, composed once. */
  earnedBy: string
  unlocks: string
  reviewRights: string
  holders: Holders
  expires: Expiry
  /**
   * The tracked skill the badge proves, named only when it is not already the
   * badge's name — a row never repeats itself.
   */
  skillName: string | null
}

const badgeName = (seed: BadgeSeed): string => {
  const { name, skillKey } = seed
  if (name !== undefined) return name
  if (skillKey === undefined) throw new Error(`Badge ${seed.key} has neither a name nor a skill`)
  return skillRowFor(skillKey).name
}

export const BADGE_ROWS: readonly BadgeRow[] = BADGE_SEEDS.map((seed) => {
  const name = badgeName(seed)
  const skillName = seed.skillKey === undefined ? null : skillRowFor(seed.skillKey).name
  return {
    key: seed.key,
    name,
    earnedBy: `${seed.course} · ${seed.supervised} supervised ${seed.supervisedUnit} · expert sign-off`,
    unlocks: seed.unlocks,
    reviewRights: seed.reviewRights,
    holders: holdersOf(seed),
    expires: seed.expires,
    skillName: skillName === name ? null : skillName,
  }
})

/** Badges tied to an Odoo release, and the one tied to an agent version. */
export const ODOO_BOUND_ROWS = BADGE_ROWS.filter((row) => row.expires === 'With the Odoo version')
export const AGENT_BOUND_ROWS = BADGE_ROWS.filter((row) => row.expires === 'Per agent version')

export const BADGES_TABLE = {
  caption: `The ${BADGE_ROWS.length} badges the group awards: how each one is earned, what it unlocks in allocation, the review rights it carries, who holds it today and when it lapses.`,
  columns: {
    badge: 'Badge',
    earnedBy: 'Earned by',
    unlocks: 'Unlocks in allocation',
    reviewRights: 'Review rights',
    holders: 'Holders',
    expires: 'Expires',
  },
  /** The unlock column is a claim about another screen — it links there. */
  unlocksNote: { label: 'in the match', href: ALLOCATIONS_HREF },
  skillPrefix: 'proves',
  skillHref: SKILLS_HREF,
  holdersMore: (more: number) => `+${more} more`,
  holdersLabel: (count: number) => (count === 1 ? '1 holder' : `${count} holders`),
  noExpiry: '—',
} as const

/* ── 3. Where the badge shows up (the allocation match) ─────────────────── */

const candidateTagged = (tag: MatchCandidate['tag']): MatchCandidate => {
  const candidate = MATCH_CANDIDATES.find((entry) => entry.tag === tag)
  if (candidate === undefined) throw new Error(`No ${tag} candidate in the seeded match`)
  return candidate
}

const BEST_FIT = candidateTagged('Best fit')
const GROWTH_MOVE = candidateTagged('Growth move')

const BEST_FIT_ID = BEST_FIT.personId
if (BEST_FIT_ID === null) {
  throw new Error('The best fit in the seeded match is not a seeded person — no badge can back it')
}

const MIGRATION_BADGE = BADGE_ROWS.find((row) => row.key === 'odoo-data-migration')
if (MIGRATION_BADGE === undefined) {
  throw new Error('The migration badge is missing — the allocation panel has nothing to point at')
}
if (!MIGRATION_BADGE.holders.named.some((holder) => holder.id === BEST_FIT_ID)) {
  throw new Error(
    `The match ranks ${BEST_FIT.label} best fit, but ${MIGRATION_BADGE.name} does not name them a holder`
  )
}
if (GROWTH_MOVE.personId !== LADDER_MARKER.personId) {
  throw new Error('The growth move and the ladder marker are different people — the panel would lie')
}

export const MATCH_PANEL = {
  label: 'WHERE THE BADGE SHOWS UP',
  prose: [
    { kind: 'text', text: 'On the open migration task PX ranks ' },
    { kind: 'link', label: BEST_FIT.label, href: personHref(BEST_FIT_ID) },
    {
      kind: 'text',
      text: ` ${BEST_FIT.tag.toLowerCase()} at ${BEST_FIT.rateLabel}: he holds the ${MIGRATION_BADGE.name} badge at ${CERTIFIED.label}, so the hours can be routed to him unsupervised. `,
    },
    { kind: 'link', label: GROWTH_MOVE.label, href: LADDER_MARKER.href },
    {
      kind: 'text',
      text: ` sits one rung below at ${ASSISTED.label} — ${ASSISTED.meaning} — which is why the same badge makes her the ${GROWTH_MOVE.tag.toLowerCase()} at ${GROWTH_MOVE.rateLabel}, with ${PAIRING_HOURS} h of review priced in. The tag and the rate are what a badge buys; the ranking itself is in `,
    },
    { kind: 'link', label: 'the match', href: ALLOCATIONS_HREF },
    { kind: 'text', text: '.' },
  ] satisfies readonly Segment[],
} as const

/* ── 4. Verification ────────────────────────────────────────────────────── */

export const VERIFICATION_PANEL = {
  label: 'VERIFICATION',
  prose: [
    { kind: 'text', text: 'Three records stand behind every award, and PX shows no badge without all three: the course record from the ' },
    { kind: 'link', label: 'training center', href: TRAINING_HREF },
    {
      kind: 'text',
      text: `, the supervised deliveries with their close-out rating out of ${RATING_SCALE}, and the sign-off of an expert — on the architecture and retail badges that is `,
    },
    { kind: 'link', label: EXPERT.name, href: personHref(EXPERT.id) },
    { kind: 'text', text: `, ${EXPERT.role.toLowerCase()}. A badge is never self-declared, and a course alone never earns one.` },
  ] satisfies readonly Segment[],
} as const

/* ── 5. Badges expire ───────────────────────────────────────────────────── */

const ODOO_VERSIONS = [...new Set(PROJECTS.map((project) => project.odooVersion))].sort(
  (a, b) => b - a
)
const LATEST_VERSION = ODOO_VERSIONS[0]
const PREVIOUS_VERSION = ODOO_VERSIONS[1]
if (LATEST_VERSION === undefined || PREVIOUS_VERSION === undefined) {
  throw new Error('The client estate runs a single Odoo version — expiry has no base to read')
}

const systemsOn = (version: number): number =>
  PROJECTS.filter((project) => project.odooVersion === version).length

export const VERSION_BASE = {
  latest: LATEST_VERSION,
  previous: PREVIOUS_VERSION,
  latestCount: systemsOn(LATEST_VERSION),
  previousCount: systemsOn(PREVIOUS_VERSION),
  total: PROJECTS.length,
} as const

export const EXPIRY_PANEL = {
  label: 'BADGES EXPIRE',
  prose: [
    {
      kind: 'text',
      text: `A version-bound badge lapses when Odoo drops support for that release: ${ODOO_BOUND_ROWS.length} of the ${BADGE_ROWS.length} badges above are written that way, and ${AGENT_BOUND_ROWS.length} more lapses with its agent's version. `,
    },
    {
      kind: 'link',
      label: `${VERSION_BASE.latestCount} of the ${VERSION_BASE.total} client systems`,
      href: CLIENTS_HREF,
    },
    {
      kind: 'text',
      text: ` run Odoo ${VERSION_BASE.latest} and ${VERSION_BASE.previousCount} still run ${VERSION_BASE.previous}, so the annual release arrives in the training calendar as a dated trigger rather than a surprise: every holder of a lapsing badge takes a seat before support ends.`,
    },
  ] satisfies readonly Segment[],
} as const

/* ── Page copy ──────────────────────────────────────────────────────────── */

export const BADGES_COPY = {
  title: 'Badges',
  lede: `What a badge changes: which work PX may route to a person, at which rate, and who may review it — ${BADGE_ROWS.length} definitions across ${LEVELS.length} levels.`,
  densityNote: `The ${BADGE_ROWS.length} definitions above are group-wide; who holds which badge, at which level and since when is a fact about a person and lives on their profile, reached from any name here. Expiry forecasts are withheld until the Odoo ${VERSION_BASE.latest + 1} date firms — a lapse date guessed off an unannounced release would book training against a week that may not exist.`,
} as const
